import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchConversations, fetchMessages, sendMessage, selectConversations, selectSelectedConversation, selectMessages, selectMessagesByConversation, selectMessagesLoading, selectConversation } from '../store/AdminSlices/messageSlice';
import { CardSkeleton } from '../components/Skeleton';

// Import types from messageSlice
type Message = {
  id: string;
  sender_id: string;
  sender_name: string;
  sender_type: 'admin' | 'student' | 'coach';
  content: string;
  created_at: string;
  is_read: boolean;
  temp_id?: string;
  pending?: boolean;
}

type Conversation = {
  id: string;
  user_id: string;
  user_name: string;
  user_type: 'student' | 'coach';
  user_avatar?: string;
  last_message: string;
  last_message_time: string;
  unread_count: number;
  is_active: boolean;
}

const MessagesPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const conversations = useAppSelector(selectConversations) as Conversation[];
  const selectedConversation = useAppSelector(selectSelectedConversation) as Conversation | null;
  const messages = useAppSelector(selectMessages) as Message[];
  const messagesByConversation = useAppSelector(selectMessagesByConversation) as Record<string, Message[]>;
  const loading = useAppSelector(selectMessagesLoading);

  const [activeTab, setActiveTab] = useState<'all' | 'students' | 'coaches'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [messageInput, setMessageInput] = useState('');
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [messageLoading, setMessageLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    dispatch(fetchConversations(activeTab)).then(() => {
      setIsInitialLoad(false);
    });
  }, [dispatch, activeTab]);

  // Handle conversation ID or userId from URL query parameter
  useEffect(() => {
    const conversationId = searchParams.get('id');
    const userId = searchParams.get('userId');
    const userType = searchParams.get('userType') as 'student' | 'coach' | undefined;

    if (conversationId) {
      // If conversation ID is provided, fetch messages directly
      if (selectedConversation?.id !== conversationId && selectedConversation?.user_id !== conversationId) {
        dispatch(fetchMessages(conversationId));
      }
    } else if (userId && userType && conversations.length > 0) {
      // If userId and userType are provided, find the matching conversation
      const matchingConversation = conversations.find(
        (conv) => conv.user_id === userId && conv.user_type === userType
      );
      if (matchingConversation && selectedConversation?.id !== matchingConversation.id) {
        // Found matching conversation, fetch its messages
        dispatch(fetchMessages(matchingConversation.id));
      }
    } else if (!conversationId && !userId && conversations.length > 0 && !selectedConversation) {
      // No URL params and no selected conversation, auto-select first conversation
      const firstConversation = conversations[0];
      if (firstConversation) {
        dispatch(fetchMessages(firstConversation.id));
      }
    }
  }, [searchParams, selectedConversation?.id, conversations, selectedConversation, dispatch]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages, selectedConversation]);

  const handleConversationClick = (conversation: Conversation) => {
    // Update URL with conversation ID
    navigate(`/messages?id=${conversation.id}`, { replace: true });
    
    if (messagesByConversation[conversation.id]) {
      // Already have cached messages, show immediately
      dispatch(selectConversation(conversation.id));
    } else {
      // Don't have cached messages yet, load them
      setMessageLoading(true);
      dispatch(fetchMessages(conversation.id)).finally(() => {
        setMessageLoading(false);
      });
    }
  };

  const handleSendMessage = async () => {
    if (!messageInput.trim() || !selectedConversation) return;

    const tempId = `temp-${Date.now()}`;
    await dispatch(sendMessage({
      conversation_id: selectedConversation.id,
      content: messageInput,
      temp_id: tempId,
    }));

    setMessageInput('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const filteredConversations = conversations.filter((conv) =>
    conv.user_name.toLowerCase().includes(searchQuery.toLowerCase())
  );


  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 48) {
      return '1d ago';
    } else {
      return `${Math.floor(diffInHours / 24)}d ago`;
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Filter Tabs - Always on top */}
      <div className="flex gap-6 px-6  bg-white">
        <button
          onClick={() => setActiveTab('all')}
          className={`text-sm font-medium pb-3 border-b-2 transition-colors ${
            activeTab === 'all'
              ? 'text-gray-900 border-gray-900'
              : 'text-gray-500 border-transparent hover:text-gray-700'
          }`}
        >
          All
        </button>
        <button
          onClick={() => setActiveTab('students')}
          className={`text-sm font-medium pb-3 border-b-2 transition-colors ${
            activeTab === 'students'
              ? 'text-gray-900 border-gray-900'
              : 'text-gray-500 border-transparent hover:text-gray-700'
          }`}
        >
          Students
        </button>
        <button
          onClick={() => setActiveTab('coaches')}
          className={`text-sm font-medium pb-3 border-b-2 transition-colors ${
            activeTab === 'coaches'
              ? 'text-gray-900 border-gray-900'
              : 'text-gray-500 border-transparent hover:text-gray-700'
          }`}
        >
          Coaches
        </button>
      </div>

      <div className="flex-1 flex overflow-hidden gap-2.5 p-2.5">
        {/* Conversations List */}
        <div className={`${selectedConversation ? 'w-80' : 'w-full'} border border-gray-100 rounded-lg bg-white flex flex-col shadow-sm`}>

          {/* Search */}
          <div className="px-6 py-4">
            <div className="relative border border-gray-100 rounded-lg overflow-hidden hover:border-gray-200 transition-colors focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent">
              <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search conversations"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 bg-white border-0 rounded-lg text-sm text-gray-700 placeholder-gray-400 focus:outline-none"
              />
            </div>
          </div>

          {/* Conversations */}
          <div className="flex-1 overflow-y-auto">
            {isInitialLoad && loading ? (
              <div className="p-4 space-y-2">
                {[1, 2, 3].map((i) => (
                  <CardSkeleton key={i} />
                ))}
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-sm text-gray-400">No conversations found</p>
              </div>
            ) : (
              filteredConversations.map((conversation) => (
                <button
                  key={conversation.id}
                  onClick={() => handleConversationClick(conversation)}
                  className={`w-full px-6 py-4 flex items-start gap-3 hover:bg-gray-50 transition-colors text-left ${
                    selectedConversation?.id === conversation.id ? 'bg-gray-50' : ''
                  }`}
                >
                  <div className="relative flex-shrink-0">
                    <img
                      src={conversation.user_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(conversation.user_name)}&background=random`}
                      alt={conversation.user_name}
                      className="h-12 w-12 rounded-full object-cover"
                    />
                    {conversation.unread_count > 0 && (
                      <div className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 rounded-full flex items-center justify-center text-xs font-semibold text-white">
                        {conversation.unread_count}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between mb-1">
                      <h3 className="text-sm font-semibold text-gray-900 truncate">{conversation.user_name}</h3>
                      <span className="text-xs text-gray-400 ml-2 flex-shrink-0">{formatTime(conversation.last_message_time)}</span>
                    </div>
                    <p className="text-sm text-gray-500 truncate">{conversation.last_message}</p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Chat Area */}
        {selectedConversation && 
          <div className="flex-1 flex flex-col bg-white border border-gray-100 rounded-lg shadow-sm overflow-hidden">
            <>
              {/* Chat Header */}
              <div className="bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img
                    src={selectedConversation.user_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedConversation.user_name)}&background=random`}
                    alt={selectedConversation.user_name}
                    className="h-10 w-10 rounded-full object-cover"
                  />
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">{selectedConversation.user_name}</h2>
                    {/* <p className="text-sm text-green-600">Active</p> */}
                  </div>
                </div>
                {/* <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                  </svg>
                </button> */}
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-6 py-6">
                {messageLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <div className="inline-block">
                        <div className="w-8 h-8 border-4 border-gray-200 border-t-black rounded-full animate-spin"></div>
                      </div>
                      <p className="text-sm text-gray-400 mt-3">Loading messages...</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {messages.map((message) => {
                      const isAdmin = message.sender_type === 'admin';
                      return (
                        <div key={message.id} className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-md ${message.pending ? 'opacity-70' : ''}`}>
                            <div
                              className={`rounded-2xl px-4 py-3 ${
                                isAdmin 
                                  ? 'bg-[#2D2D2D] text-white' 
                                  : 'bg-white text-gray-900 border border-gray-100'
                              }`}
                            >
                              <p className="text-sm leading-relaxed">{message.content}</p>
                            </div>
                            <p className={`text-xs mt-1.5 ${isAdmin ? 'text-right' : 'text-left'} text-gray-400`}>
                              {formatTime(message.created_at)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>

              {/* Message Input */}
              <div className="bg-white border-t border-gray-100 px-6 py-4">
                <div className="flex items-center gap-3">
                  {/* <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                  </button> */}
                  <input
                    type="text"
                    placeholder="Type your message..."
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="flex-1 px-4 py-2.5 bg-gray-50 border-0 rounded-[6px] text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-200"
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={!messageInput.trim()}
                    className="p-2.5 bg-black text-white rounded-xl hover:bg-black-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg className="w-5 h-5 rotate-45" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  </button>
                </div>
              </div>
            </>
          </div>
}
      </div>
    </div>
  );
};

export default MessagesPage;
